export const MAX_COMPANION_SLOTS = 50;

const normalizeNameRow = (row = {}) => ({
  id: typeof row === 'object' ? row?.id || null : null,
  name: typeof row === 'string' ? row : row?.name || '',
});

export const createEmptyGuestNameRows = () => [{ id: null, name: '' }];

export const buildGuestNameSlots = (namedRows = [], unnamedCount = 0) => {
  const knownNames = namedRows
    .map(normalizeNameRow)
    .filter((row) => row.name.trim());
  const rows = knownNames.length ? knownNames : createEmptyGuestNameRows();
  const parsedUnnamedCount = Number.parseInt(unnamedCount, 10);
  const blankSlots = Math.max(0, Number.isNaN(parsedUnnamedCount) ? 0 : parsedUnnamedCount);

  return [
    ...rows,
    ...Array.from({ length: blankSlots }, () => ({ id: null, name: '' })),
  ];
};

export const getCompanionSlotCount = (rows = []) => Math.max(0, rows.length - 1);

export const getNamedCompanionCount = (rows = []) => (
  rows.slice(1).filter((row) => normalizeNameRow(row).name.trim()).length
);

export const getGuestPartyCount = (guests = []) => guests.reduce(
  (total, guest) => {
    const accompanyingCount = Number.parseInt(
      guest?.accompanying_guests || 0,
      10,
    );
    return total + 1 + (
      Number.isNaN(accompanyingCount)
        ? 0
        : Math.max(accompanyingCount, 0)
    );
  },
  0,
);

export const getGuestsNeedingRoomCount = (guests = []) => getGuestPartyCount(
  guests.filter((guest) => guest?.requires_room !== false),
);

export const resizeCompanionSlots = (rows = [], requestedCount = 0) => {
  const normalizedRows = rows.length
    ? rows.map(normalizeNameRow)
    : createEmptyGuestNameRows();
  const anchor = normalizedRows[0];
  const companionRows = normalizedRows.slice(1);
  const parsedCount = Number.parseInt(requestedCount, 10);
  const namedMinimum = getNamedCompanionCount(normalizedRows);
  const targetCount = Math.max(
    namedMinimum,
    Math.min(MAX_COMPANION_SLOTS, Number.isNaN(parsedCount) ? namedMinimum : parsedCount),
  );

  if (targetCount > companionRows.length) {
    return [
      anchor,
      ...companionRows,
      ...Array.from(
        { length: targetCount - companionRows.length },
        () => ({ id: null, name: '' }),
      ),
    ];
  }

  const resizedCompanions = [...companionRows];
  for (let index = resizedCompanions.length - 1; index >= 0 && resizedCompanions.length > targetCount; index -= 1) {
    if (!resizedCompanions[index].name.trim()) {
      resizedCompanions.splice(index, 1);
    }
  }

  return [anchor, ...resizedCompanions];
};

export const removeCompanionSlot = (rows = [], index) => {
  if (index <= 0 || index >= rows.length) return rows;
  return rows.filter((_row, rowIndex) => rowIndex !== index);
};

export const serializeGuestNameSlots = (rows = []) => {
  const normalizedRows = rows.length
    ? rows.map(normalizeNameRow)
    : createEmptyGuestNameRows();
  const names = normalizedRows
    .map((row) => ({ ...row, name: row.name.trim() }))
    .filter((row) => row.name);
  const unnamedCompanionCount = normalizedRows
    .slice(1)
    .filter((row) => !row.name.trim())
    .length;

  return {
    anchorNameMissing: !normalizedRows[0].name.trim(),
    names,
    unnamedCompanionCount,
    companionSlotCount: getCompanionSlotCount(normalizedRows),
    totalPartySize: names.length + unnamedCompanionCount,
  };
};
